import { faExternalLink, faVideo } from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { FC, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Dialog } from 'src/components/Dialog';
import { Grid } from 'src/components/Grid';
import { Icon } from 'src/components/Icon';
import { Link } from 'src/components/Link';
import { Stack } from 'src/components/Stack';
import { styled } from 'src/components/muiTheme/utils';
import { useSession } from 'src/contexts/SessionContext';
import { externalLinks } from 'src/externalLinks';
import { FeatureFlag } from 'src/models/platform/_types';
import { fetchUserAuthorizations } from 'src/models/user/user';
import { fetchPlanWorkoutsByIds } from 'src/models/workout-plan/workout-plan';
import { RecurringDescription } from 'src/shared/Appointments/RecurringDescription';
import { hasFeatureFlag } from 'src/shared/usePermissionCheck';

import { ZoomSettingsPanel } from './ZoomSettingsPanel';

import type { Appointment } from 'src/models/appointment/_types';

interface SessionSummaryProps {
  appointment: Appointment;
  onAppointmentUpdate?: (appointment: Appointment) => void;
}

const InfoRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '80px 1fr',
  gap: theme.spacing(1.5),
  fontSize: '0.8125rem',
  lineHeight: 1.4,
  '& .label': {
    color: theme.vars.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  '& .value': {
    fontWeight: 500,
    minWidth: 0,
    wordBreak: 'break-word',
    // Override nested Typography (e.g., RecurringDescription) but preserve Link colors
    '& .MuiTypography-root:not(.MuiLink-root)': {
      fontSize: 'inherit',
      fontWeight: 'inherit',
      color: 'inherit',
      lineHeight: 'inherit',
    },
  },
}));

type InfoItem = { label: string; value: ReactNode; fullWidth?: boolean };

/**
 * SessionSummary - Compact read-only display of session details
 */
export const SessionSummary: FC<SessionSummaryProps> = ({
  appointment,
  onAppointmentUpdate,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'calendar.viewEventDialog.summary' });
  const { session } = useSession();
  const startDate = new Date(appointment.startTime);
  const endDate = new Date(appointment.endTime);

  const activeVisits =
    appointment.visits?.filter(
      (v) => v.status !== 'cancelled' && v.status !== 'late_cancelled'
    ) || [];
  const checkedInVisits =
    appointment.visits?.filter((v) => v.status === 'completed') || [];
  const serviceHasWaitlist = appointment.service?.waitlist ?? false;

  const { data: partners = [] } = useQuery({
    queryKey: ['partners'],
    queryFn: fetchUserAuthorizations,
  });
  const zoomConnected = partners.some((p) => p.source === 'zoom');

  // Fetch plan workouts if planWorkoutIds exists
  const hasPlanWorkoutIds =
    appointment.planWorkoutIds && appointment.planWorkoutIds.length > 0;
  const { data: planWorkouts = [], isLoading: planWorkoutsLoading } = useQuery({
    queryKey: ['planWorkouts', appointment.planWorkoutIds],
    queryFn: () => fetchPlanWorkoutsByIds(appointment.planWorkoutIds),
    enabled: hasPlanWorkoutIds,
  });

  const [showZoomSettings, setShowZoomSettings] = useState(false);

  // Build items array for grid
  const items: InfoItem[] = [
    { label: t('date'), value: format(startDate, 'EEE, MMM d, yyyy') },
    {
      label: t('time'),
      value: `${format(startDate, 'h:mm a')} – ${format(endDate, 'h:mm a')}`,
    },
  ];

  if (appointment.locationName) {
    items.push({ label: t('location'), value: appointment.locationName });
  }

  if (appointment.primaryTrainerNames?.length) {
    items.push({
      label: t('primaryStaff'),
      value: appointment.primaryTrainerNames.join(', '),
    });
  }

  if (appointment.secondaryTrainerNames?.length) {
    items.push({
      label: t('secondaryStaff'),
      value: appointment.secondaryTrainerNames.join(', '),
    });
  }

  // Attendance
  const attendanceText = [
    `${activeVisits.length} ${t('booked')}`,
    `${checkedInVisits.length} ${t('checkedIn')}`,
    ...(serviceHasWaitlist ? [`${appointment.entries?.length || 0} ${t('waitlist')}`] : []),
  ].join(' · ');
  items.push({ label: t('attendance'), value: attendanceText });

  // Resources
  if (hasFeatureFlag(session, FeatureFlag.ResourceManagement)) {
    if (appointment.noResources) {
      items.push({ label: t('assets'), value: t('disabled') });
    } else if (appointment.resourceNames?.length) {
      items.push({
        label: t('assets'),
        value: appointment.resourceNames.join(', '),
      });
    }
  }

  // Full-width items
  if (appointment.scheduledAppointmentId && appointment.scheduledAppointment) {
    items.push({
      label: t('recurring'),
      value: (
        <RecurringDescription
          startAt={appointment.scheduledAppointment.startAt}
          endAt={appointment.scheduledAppointment.endAt}
          recurringType={appointment.scheduledAppointment.recurringType}
          recurringDayNumbers={appointment.scheduledAppointment.recurringDayNumbers}
          recurringCount={appointment.scheduledAppointment.recurringCount}
          days={appointment.scheduledAppointment.days}
          inline
        />
      ),
      fullWidth: true,
    });
  }

  if (appointment.zoomMeeting?.joinUrl) {
    items.push({
      label: t('zoomUrl'),
      value: (
        <Link
          href={appointment.zoomMeeting.joinUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
          {t('joinZoom')}{' '}
          <Icon icon={faExternalLink} size="tiny" />
        </Link>
      ),
    });
  }

  if (appointment.zoom?.password) {
    items.push({
      label: t('zoomPassword'),
      value: appointment.zoom.password,
    });
  }

  if (appointment.description) {
    items.push({
      label: t('description'),
      value: (
        <span style={{ whiteSpace: 'pre-wrap' }}>{appointment.description}</span>
      ),
      fullWidth: true,
    });
  }

  if (appointment.notes) {
    items.push({
      label: t('notes'),
      value: (
        <span style={{ whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>
          {appointment.notes}
        </span>
      ),
      fullWidth: true,
    });
  }

  // Workout links - matches v2 lines 503-517
  if (hasPlanWorkoutIds) {
    items.push({
      label: t('workouts'),
      value: planWorkoutsLoading ? (
        <span style={{ color: 'var(--mui-palette-text-secondary)' }}>
          {t('loading')}
        </span>
      ) : (
        <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
          {planWorkouts.map((workout) => (
            <Link
              key={workout.id}
              href={externalLinks.editPlanWorkout(appointment.serviceId, workout.id)}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              {workout.name}
              <Icon icon={faExternalLink} size="tiny" />
            </Link>
          ))}
        </Stack>
      ),
      fullWidth: true,
    });
  }

  // Edit Workout Calendar link - matches v2 lines 529-541
  if (appointment.calendarId) {
    items.push({
      label: t('workouts'),
      value: (
        <Link
          href={externalLinks.serviceWorkoutCalendar(appointment.serviceId)}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >

          {t('editWorkoutCalendar')}
          <Icon icon={faExternalLink} size="tiny" />
        </Link>
      ),
      fullWidth: true,
    });
  }

  return (
    <Stack spacing={1.5}>
      {/* All items in responsive grid */}
      <Grid container spacing={{ xs: 0.5, sm: 1 }}>
        {items.map((item, idx) => (
          <Grid key={idx} size={{ xs: 12, sm: item.fullWidth ? 12 : 6 }}>
            <InfoRow>
              <span className="label">{item.label}</span>
              <span className="value">{item.value}</span>
            </InfoRow>
          </Grid>
        ))}

        {/* Zoom toggle */}
        {zoomConnected && (
          <Grid size={12}>
            <InfoRow>
              <span className="label">{t('zoom')}</span>
              <Link
                component="button"
                type="button"
                onClick={() => setShowZoomSettings(true)}
                className="value"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
              >
                {appointment.zoomMeeting?.joinUrl ? (
                  <>
                    <Icon icon={faVideo} size="xsmall" />
                    {t('manageSettings')}
                  </>
                ) : (
                  <>
                    <Icon icon={faVideo} size="xsmall" />
                    {t('createMeeting')}
                  </>
                )}
              </Link>
            </InfoRow>
          </Grid>
        )}
      </Grid>

      {/* Zoom Settings Dialog */}
      <Dialog
        open={showZoomSettings}
        onClose={() => setShowZoomSettings(false)}
        title={t('zoomSettings')}
        maxWidth="sm"
      >
        <ZoomSettingsPanel
          appointment={appointment}
          onClose={() => setShowZoomSettings(false)}
          onAppointmentUpdate={onAppointmentUpdate}
          existingSettings={
            appointment.zoom
              ? {
                  password: appointment.zoom.password || '',
                  description: appointment.zoom.description || '',
                  autoRecording: appointment.zoom.autoRecording || 'none',
                  muteUponEntry: appointment.zoom.muteUponEntry || false,
                  waitingRoom: appointment.zoom.waitingRoom || false,
                  joinBeforeHost: appointment.zoom.joinBeforeHost || false,
                }
              : undefined
          }
        />
      </Dialog>
    </Stack>
  );
};
